<template>
    <button :class="['no-select tds-button tds-button-' + buttonStyle, {inactive}]"
            :style="{width: width}"
            :type="type"
            @click.stop="click($event)">
                <span>
                    <slot></slot>
                </span>
        <div class="spinner-wrapper ml-4" v-if="showSpinner">
            <TDSSpinner :size="20" />
        </div>
    </button>
</template>

<script lang="ts">

import {Component, Prop, Vue} from "vue-property-decorator";
import TDSSpinner from "@/components/common/TDSSpinner.vue";

@Component({
    components: {
        TDSSpinner
    }
})
export default class TDSButton extends Vue {
    @Prop({
        required: true,
        validator: function (value: string) {
            return ["primary", "secondary", "tertiary", "single-sign", "round-red", "primary-red", "secondary-red", "link", "export-link", "icon-square"].indexOf(value) !== -1;
        }
    }) buttonStyle!: string;
    @Prop({type: Boolean, default: false}) inactive!: boolean;
    @Prop({type: String}) width!: string;
    @Prop({type: String, default: "submit"}) type!: string;
    @Prop({type: Boolean, default: false}) showSpinner!: boolean;

    private click(e: Event) {
        if (this.inactive) return;
        this.$emit("click", e);
    }
}
</script>

<style lang="scss" scoped>

@keyframes animate-width {
    0% {
        transform: translateX(20px);
        opacity: 0;
    }
    100% {
        transform: translateX(0);
        opacity: 1;
    }
}

.tds-button {
    font-size: 14px;
    padding: 0 16px;
    text-align: center;
    letter-spacing: -0.3325px;
    height: 40px;
    border: none;
    border-radius: 3px;
    font-family: "FFMark W04", "Helvetica Neue", Arial, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        position: relative;
        top: -2px;
        display: flex;
        align-items: center;
        white-space: nowrap;

        img.button-icon {
            height: 15px;
            width: 15px;
            position: relative;
            top: 2px;
        }

        img.button-icon-big {
            height: 20px;
            width: 20px;
            position: relative;
            top: 2px;
        }
    }

    .spinner-wrapper {
        animation-name: animate-width;
        animation-duration: 0.3s;
        animation-iteration-count: 1;
        animation-timing-function: ease-in-out;
        overflow: hidden;

        ::v-deep .spinner {
            border-color: white;
        }
    }

    &.inactive {
        background-color: $greyShade3;
        pointer-events: none;
    }

    &:focus {
        outline: none;
    }

    @media only screen and (max-width: $breakPoint) {
        width: 100% !important;
        height: 44px;
        font-size: 16px;
    }
}

.tds-button-primary {
    min-width: 185px;
    background: $fullGrey;
    font-weight: bold;
    color: white;
}

.tds-button-secondary {
    min-width: 185px;
    text-decoration-line: underline;
    background: none;
    color: #FFF;
}

.tds-button-tertiary {
    background: none;
    border-radius: 10px;
    line-height: 19px;
    text-align: right;
    letter-spacing: -0.22px;
    color: #FFF;
    display: flex;
    font-weight: bold;
}

.tds-button-primary-red {
    background: $highlightColor;
    font-weight: bold;
    color: white;
    min-width: 185px;

    * {
        color: white;
        font-weight: bold;
    }

    &.inactive {
        background-color: $greyShade3;
    }
}

.tds-button-secondary-red {
    border-radius: 5px;
    background-color: $highlightColor;
    color: white;
    width: 100%;
    font-weight: 500;
    letter-spacing: -0.22px;
}

.tds-button-single-sign {
    background: $greyShade1;
    border: 1px solid $greyShade3;
    border-radius: 3px;
    height: 15px;
    width: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    padding: 5px;

    &:active {
        color: $greyShade5;
    }
}

.tds-button-round-red {
    background: $highlightColorShade3;
    border-radius: 18px;
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    @media only screen and (max-width: $breakPoint) {
        width: 40px !important;
        height: 40px;
        border-radius: 19px;
    }
}

.tds-button-link {
    background: none;
    text-decoration: underline;
    color: $highlightColor;
    font-weight: 500;
    padding: 0;
    justify-content: flex-start;

    &.inactive {
        background: none;
        color: $greyShade5;
    }
}

.tds-button-export-link {
    background: none;
    text-decoration: underline;
    color: $greyShade4;
    font-weight: 500;
    display: flex;
    @media only screen and (max-width: $breakPoint) {
        width: fit-content !important;
    }
}

.tds-button-icon-square {
    width: 36px !important;
    height: 36px;
    background-color: white;
    border-radius: 3px;
    border: solid 1px $greyShade3;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
